/**
 * useExportInvoicesView - Export Excel de shipments de una factura
 *
 * POST /api/v1/invoices/invoices/:invoiceId/shipments/export
 * Body: { columns: string[] }
 * Query: mismos que GET shipments (sorting, page, per_page, search, advanced_filters)
 */

import { useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { InvoicesViewApiAdapter } from '../adapters/InvoicesViewApiAdapter';

const ALLOWED_EXPORT_COLUMNS = new Set([
  'id',
  'order_id',
  'ref',
  'shop_name',
  'team_name',
  'type',
  'courier_name',
  'weight_courier_kg',
  'weight_internal_kg',
  'country',
  'city',
  'zip',
  'num_items',
  'sequence',
  'shipping_date',
  'period',
  'financial_status',
  'shipment_status',
  'pre_invoice_code',
  'invoice_erp_number',
  'tracking_number',
  'order_total',
  'date_tracking_update',
  'last_status_at',
  'line_shipping_base_amount',
  'line_shipping_total_amount',
  'line_shipping_calculation_base',
  'line_preparation_base_amount',
  'line_preparation_total_amount',
  'line_preparation_calculation_base',
  'line_cod_base_amount',
  'line_cod_total_amount',
  'line_cod_calculation_base',
  'line_other_base_amount',
  'line_other_total_amount',
  'line_other_calculation_base',
  'line_boxes_base_amount',
  'line_boxes_total_amount',
  'line_boxes_calculation_base',
  'lines_json',
]);

const FIELD_TO_EXPORT_COLUMN: Record<string, string> = {
  id: 'id',
  order_id: 'order_id',
  ref: 'ref',
  shop: 'shop_name',
  client: 'team_name',
  type: 'type',
  courier: 'courier_name',
  country: 'country',
  city: 'city',
  zip: 'zip',
  num_items: 'num_items',
  sequence: 'sequence',
  weight_internal_kg: 'weight_internal_kg',
  weight_courier_kg: 'weight_courier_kg',
  processed_at: 'shipping_date',
  date_tracking_update: 'date_tracking_update',
  last_status_at: 'last_status_at',
  period: 'period',
  financial_status: 'financial_status',
  logistic_status: 'shipment_status',
  pre_invoice_id: 'pre_invoice_code',
  invoice_id: 'invoice_erp_number',
  order_total: 'order_total',
  tracking_number: 'tracking_number',
  shipping_base: 'line_shipping_base_amount',
  preparation_base: 'line_preparation_base_amount',
  cod_base: 'line_cod_base_amount',
  other_base: 'line_other_base_amount',
  boxes_base: 'line_boxes_base_amount',
};

/** Campos visibles en la tabla shipping (useAgGridColumns) que tienen columna de export. */
const SHIPPING_TABLE_FIELDS = [
  'ref',
  'order_id',
  'shop',
  'client',
  'order_total',
  'processed_at',
  'type',
  'logistic_status',
  'courier',
  'tracking_number',
  'country',
  'city',
  'zip',
  'num_items',
  'weight_internal_kg',
  'period',
  'shipping_base',
  'preparation_base',
  'cod_base',
  'financial_status',
  'pre_invoice_id',
  'invoice_id',
] as const;

const mapTableFieldsToExportColumns = (fields: readonly string[]): string[] => {
  const columns: string[] = ['id'];

  for (const field of fields) {
    const mapped = FIELD_TO_EXPORT_COLUMN[field];
    if (mapped && ALLOWED_EXPORT_COLUMNS.has(mapped) && !columns.includes(mapped)) {
      columns.push(mapped);
    }
    if (field === 'weight_internal_kg' && !columns.includes('weight_courier_kg')) {
      columns.push('weight_courier_kg');
    }
  }

  return columns;
};

const DEFAULT_EXPORT_COLUMNS = mapTableFieldsToExportColumns(SHIPPING_TABLE_FIELDS);

const SORT_FIELD_TO_API: Record<string, string> = {
  processed_at: 'shipping_date',
  logistic_status: 'shipment_status',
  shop: 'shop_name',
  client: 'team_name',
  courier: 'courier_name',
};

export interface ExportInvoicesViewArgs {
  invoiceId: number;
  search?: string;
  searchFilter?: { filters: any[]; logic: string };
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  selectedColumns?: string[];
}

export const useExportInvoicesView = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('invoicesViewModule');

  const exportInvoicesView = async ({
    invoiceId,
    search,
    searchFilter,
    sorting,
    selectedColumns,
  }: ExportInvoicesViewArgs) => {
    if (!invoiceId || !Number.isFinite(invoiceId)) {
      toast({
        title: t('export.error.title'),
        description: t('export.error.invalidInvoice', 'Factura inválida'),
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new InvoicesViewApiAdapter(httpClient);

      const sortFieldRaw = sorting?.field || 'id';
      const sortField = SORT_FIELD_TO_API[sortFieldRaw] ?? sortFieldRaw;
      const sortDirection = sorting?.direction || 'desc';

      const columns =
        selectedColumns && selectedColumns.length > 0
          ? mapTableFieldsToExportColumns(selectedColumns)
          : DEFAULT_EXPORT_COLUMNS;

      const response = await adapter.exportInvoiceShipments(
        {
          invoiceId,
          search,
          searchFilter,
          sortBy: sortField,
          sortDirection,
          page: 1,
          limit: 25,
        },
        columns,
      );

      if (response.url_file) {
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title'),
          description: t('export.success.description'),
          variant: 'default',
        });
      } else {
        throw new Error('No se recibió URL del archivo');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('export.error.unexpected');
      toast({
        title: t('export.error.title'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportInvoicesView,
    isExporting,
  };
};
